import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface VehicleSwapProposalProps {
  vehicleTitle: string;
  onClose: () => void;
}

export default function VehicleSwapProposal({ vehicleTitle, onClose }: VehicleSwapProposalProps) {
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [message, setMessage] = useState('');
  const [cashOffer, setCashOffer] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const myVehicles = [
    { name: '2019 Honda Civic EX', vin: '2HGFC2F59KH123456', mileage: '45,000' },
    { name: '2020 Toyota Camry SE', vin: '4T1B11HK5LU123456', mileage: '32,000' },
    { name: '2018 Ford F-150 XLT', vin: '1FTEW1EP5JFA12345', mileage: '68,000' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('Please sign in to send swap proposals');
        return;
      }

      // Get sender profile
      const { data: senderProfile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();

      // In a real app, you would get the recipient's email from the vehicle owner
      // For now, we'll use a placeholder
      const recipientEmail = 'owner@example.com';
      const recipientName = 'Vehicle Owner';

      // Send email notification via Edge Function
      const { data, error } = await supabase.functions.invoke('send-swap-proposal-notification', {
        body: {
          recipientEmail,
          recipientName,
          senderName: senderProfile?.full_name || user.email?.split('@')[0] || 'A user',
          itemTitle: vehicleTitle,
          itemType: 'vehicle',
          offeredItem: selectedVehicle,
          message,
          cashOffer: cashOffer ? parseFloat(cashOffer) : undefined,
          proposalId: `vehicle_proposal_${Date.now()}`,
        },
      });

      if (error) {
        console.error('Error sending notification:', error);
        // Still show success to user even if email fails
      }

      setShowSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting proposal:', error);
      alert('Failed to send proposal. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {!showSuccess ? (
          <>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Propose Vehicle Swap</h2>
                <p className="text-sm text-gray-600 mt-1">For: {vehicleTitle}</p>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Select Your Vehicle to Swap
                </label>
                <div className="space-y-3">
                  {myVehicles.map((vehicle, index) => (
                    <label
                      key={index}
                      className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedVehicle === vehicle.name
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="vehicle"
                        value={vehicle.name}
                        checked={selectedVehicle === vehicle.name}
                        onChange={(e) => setSelectedVehicle(e.target.value)}
                        className="w-5 h-5 text-teal-600 cursor-pointer mt-1"
                        required
                      />
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-20 h-16 bg-gray-200 rounded-lg flex-shrink-0"></div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 mb-1">{vehicle.name}</p>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <i className="ri-barcode-line"></i>
                              {vehicle.vin}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-speed-line"></i>
                              {vehicle.mileage} miles
                            </span>
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <button
                  type="button"
                  className="mt-3 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                >
                  + Add New Vehicle
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Cash Offer (Optional)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                  <input
                    type="number"
                    value={cashOffer}
                    onChange={(e) => setCashOffer(e.target.value)}
                    placeholder="0"
                    className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Add cash to balance the value difference if needed
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Message to Owner
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Introduce yourself, explain why you're interested in this swap, and provide any additional details about your vehicle..."
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 resize-none"
                  required
                ></textarea>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-500">
                    Be detailed and honest about your vehicle's condition
                  </p>
                  <p className="text-xs text-gray-500">{message.length}/500</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                    <i className="ri-alert-line text-amber-600"></i>
                  </div>
                  <div className="text-sm text-amber-900">
                    <p className="font-medium mb-1">Vehicle Swap Checklist</p>
                    <ul className="space-y-1 text-amber-800">
                      <li>• Verify VIN and title status before meeting</li>
                      <li>• Get a pre-purchase inspection</li>
                      <li>• Review vehicle history reports</li>
                      <li>• Arrange test drives in safe locations</li>
                      <li>• Complete proper title transfer paperwork</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-900 font-semibold rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-teal-500 text-white font-semibold rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Swap Proposal'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="p-12 text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-teal-100 rounded-full">
              <i className="ri-check-line text-4xl text-teal-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Swap Proposal Sent!</h3>
            <p className="text-gray-600">
              The vehicle owner will review your proposal and contact you soon.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
